import React from "react";
import { Box, Button, InputLabel, OutlinedInput, Stack } from "@mui/material";
import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { customColors } from "models/customColors";
import { useLogin, modes } from "./LoginForm.hook";
import { Link } from "react-router-dom";
import { Spinner } from "UI/Spinner";
import { GoBack } from "UI/GoBack";
import * as FP from "utils/fp-js";

export const AuthLink = styled(Link)`
  font-size: 14px;
  line-height: 17px;
  letter-spacing: 0em;
  color: ${customColors.mainColor};
  text-decoration: none;
`;

export const Error = styled(Box)`
  color: ${customColors.error};
  font-size: 14px;
  font-family: Inter;
  font-weight: 400;
  word-wrap: break-word;
`;

export const LoginForm = ({
  setError,
  reloadButtons,
  loading,
  setLoading,
  cancelCallback = undefined,
  title = "Log into your Dashboard",
  comment = null,
}) => {
  const {
    formData,
    passwordRegisterProps,
    usernameRegisterProps,
    otpRegisterProps,
    isChecking,
    mode,
    setMode,
    onEmailSubmit,
    onPasswordSubmit,
    onOtpSubmit,
    resendOtpCode,
  } = useLogin({ setError, reloadButtons, setLoading, cancelCallback });

  return FP.match(mode)
    .on(
      modes.LOGIN_WITH_CODE,
      <form
        key={"otp"}
        onSubmit={formData.handleSubmit(onOtpSubmit)}
        className="mui-form"
        autoComplete="off"
        noValidate
      >
        <Stack alignItems={"stretch"} gap={1}>
          <div className="flex flex-row justify-start items-center gap-1 mb-1 select-none">
            <GoBack hanldeClick={() => setMode(modes.LOGIN)} />
            <div className="text-xl sm:text-xl text-left font-bold">
              Log in with a code
            </div>
          </div>
          <div className="max-w-72 select-none mb-1">
            Enter the one-time code we sent to your email to continue.
          </div>
          <Stack alignItems={"stretch"}>
            <OutlinedInput
              id="otp"
              type={"text"}
              {...otpRegisterProps}
              autoComplete="off"
              role="presentation"
              inputProps={{
                autoComplete: "off",
                role: "presentation",
                form: {
                  autocomplete: "off",
                },
              }}
            />
            <ErrorMessage
              errors={formData.formState.errors}
              name="password"
              render={({ message }) => <Error>{message}</Error>}
            />
          </Stack>
          <Stack direction={"row"} justifyContent={"flex-end"} gap={2}>
            <button
              className="text-violet-900 text-sm hover:underline"
              type="button"
              onClick={resendOtpCode}
            >
              Resend OTP code
            </button>
          </Stack>
          <Stack direction={"row"} justifyContent={"flex-end"} gap={2}>
            {!!cancelCallback && (
              <Button variant="outlined" size="large" onClick={cancelCallback}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={isChecking || loading}
              size="large"
              fullWidth
            >
              Log In
            </Button>
          </Stack>
        </Stack>
        {isChecking && <Spinner />}
      </form>
    )
    .on(
      modes.PASSWORD,
      <form
        key="password"
        onSubmit={formData.handleSubmit(onPasswordSubmit)}
        className="mui-form"
        autoComplete="off"
        noValidate
      >
        <Stack alignItems={"stretch"} gap={1}>
          <div className="flex flex-row justify-start items-center gap-1 mb-4">
            <GoBack
              hanldeClick={() => {
                formData.setValue("password", "");
                setMode(modes.LOGIN);
              }}
            />
            <div className="text-xl sm:text-xl text-left font-bold">
              Enter your Password
            </div>
          </div>
          <Stack alignItems={"stretch"}>
            <InputLabel htmlFor="password" sx={{ color: "gray" }}>
              Password
            </InputLabel>
            <OutlinedInput
              id="password"
              type={"password"}
              {...passwordRegisterProps}
              autoComplete="off"
              role="presentation"
              inputProps={{
                autoComplete: "off",
                role: "presentation",
                form: {
                  autocomplete: "off",
                },
              }}
            />
            <ErrorMessage
              errors={formData.formState.errors}
              name="password"
              render={({ message }) => <Error>{message}</Error>}
            />
          </Stack>
          <Stack direction={"row"} justifyContent={"flex-end"} gap={2}>
            <AuthLink to="/forgot_password">Forgot password?</AuthLink>
          </Stack>
          <Stack direction={"row"} justifyContent={"flex-end"} gap={2}>
            {!!cancelCallback && (
              <Button variant="outlined" size="large" onClick={cancelCallback}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={isChecking || loading}
              size="large"
              fullWidth
            >
              Continue
            </Button>
          </Stack>
        </Stack>
        {isChecking && <Spinner />}
      </form>
    )
    .on(
      modes.LOGIN,
      <form
        key="email"
        onSubmit={formData.handleSubmit(onEmailSubmit)}
        className="mui-form"
        noValidate
      >
        <Stack alignItems={"stretch"} gap={1}>
          <div className="text-xl sm:text-xl text-center font-bold mb-2">
            {title}
          </div>
          {!!comment && (
            <div className="text-center text-gray-600 mb-2">{comment}</div>
          )}
          <Stack alignItems={"stretch"} mb={3}>
            <InputLabel htmlFor="username" sx={{ color: "gray" }}>
              Your email
            </InputLabel>
            <OutlinedInput
              id="username"
              type="email"
              {...usernameRegisterProps}
              error={!!formData.formState.errors?.username}
              autoComplete="username"
            />
            <ErrorMessage
              errors={formData.formState.errors}
              name="username"
              render={({ message }) => <Error>{message}</Error>}
            />
          </Stack>
          <Stack direction={"row"} justifyContent={"flex-end"} gap={2}>
            {!!cancelCallback && (
              <Button variant="outlined" size="large" onClick={cancelCallback}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={isChecking || loading}
              size="large"
              fullWidth
            >
              Continue
            </Button>
          </Stack>
        </Stack>
        {isChecking && <Spinner />}
      </form>
    )
    .otherwise(null);
};
